import { LocaleString } from 'groq/shared/LocaleString';
import { SitemapCategoryData } from 'types/sitemap';
import { MainNavItemLink } from 'groq/shared/MainNavItemLink';
import { removeLeadingSlash } from 'utils/removeLeadingSlash';

const categoryPriorityList = [
  'womens',
  'mens',
  'kids',
  'new-arrivals',
  'collections',
  'collaborations',
  'gift-guide',
  'sale',
  'outlet',
  'exclusion-list',
  'puma',
];

export const ArticlesQuery = `
  *[_type == 'Article' && defined(puid.current) && !($country in regionalExclusion)] {
    ${LocaleString('title')},
    "url": "articles/" + puid.current,
    availableFrom,
    availableTo,
  }
`;

const CategoriesQuery = `
*[_type == 'category' && country == $country && hasItemsInStock > 0]{
    id,
    _id,
    ${LocaleString('name')},
    url,
    online,
    onlineTo,
    onlineFrom,
    "parentCategoryId": parentCategory._ref,
    "noIndex": seo.noIndex,
}
`;

const rootCategoryQuery = `
*[_type == 'category' && country == $country && id == 'root']{
  _id,
}[0]._id
`;

const RedirectsQuery = `
  *[_type == 'UrlRedirect' && country == $country && defined(vanityUrl.current) && (defined(link) || defined(support) || defined(category) || defined(product) || defined(marketing))] {
    "vanityUrl": vanityUrl.current,
    online,
    availableFrom,
    availableTo
  }
`;

const SupportPagesQuery = `
*[_type == 'SupportPage' && country == $country && defined(url.current)] {
  "url": url.current,
  availableFrom,
  availableTo,
  ${LocaleString('title')},
  "noIndex": seo.noIndex
  }
  `;

const MarketingPagesQuery = `
  *[_type == 'MarketingPage' && country == $country && defined(url.current)] {
    "url": url.current,
    seo {
      ${LocaleString('pageTitles')},
      pageTitle,
      noIndex
    },
  }
`;

const MainNavItem = (field: string, depth = 0) => `
  ${field} {
    ${MainNavItemLink('link')},
    availableOn,
    ${
      depth > 0
        ? MainNavItem('children[availableOn == "mobileApp"]', depth - 1)
        : ''
    }
  }
`;

const MobileMainNavLinksQuery = `
  *[_type == "MainNav" && country == $country][0] {
    children[availableOn == "mobileApp"] {
      ${MainNavItemLink('link')},
      ${MainNavItem('navChildren[availableOn == "mobileApp"]')},
      ${MainNavItem('children[availableOn == "mobileApp"]', 4)},
    }
  }
`;

export const SitemapQuery = `{
    "articles": ${ArticlesQuery},
    "rootCategory": ${rootCategoryQuery},
    "categories": ${CategoriesQuery},
    "redirects": ${RedirectsQuery},
    "supportPages": ${SupportPagesQuery},
    "marketingPages": ${MarketingPagesQuery},
    "mobileMainNavLinks": ${MobileMainNavLinksQuery},
}`;

export const sortCategories = (categories?: SitemapCategoryData[]) =>
  categories?.sort((a, b) => {
    const indexA = categoryPriorityList.indexOf(a.id);
    const indexB = categoryPriorityList.indexOf(b.id);

    if (indexA === -1 && indexB === -1) {
      return 0;
    } else if (indexA === -1) {
      return 1;
    } else if (indexB === -1) {
      return -1;
    } else {
      return indexA - indexB;
    }
  });

export const buildCategoryTree = (
  categories: SitemapCategoryData[],
  parentId: string | undefined
): SitemapCategoryData[] => {
  const result: SitemapCategoryData[] = [];
  categories
    .filter(category => category.parentCategoryId === parentId)
    .forEach(category => {
      const subcategories = buildCategoryTree(categories, category._id);
      if (category.url || subcategories.some(subcategory => subcategory.url)) {
        result.push({
          ...category,
          categories: subcategories,
        });
      }
    });
  return result;
};

export const getNameFromUrl = url => {
  if (!url) {
    return '';
  }
  const urlParts = url.split('/');
  const transformedParts = urlParts.map(part =>
    part
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ')
  );
  return transformedParts.join(' ');
};

export const getNavLinksFlatArray = (navItems: any[]): string[] => {
  const flatArray: string[] = [];

  const flatten = (items: any[]) => {
    items.forEach(item => {
      if (item.link?.href) {
        flatArray.push(removeLeadingSlash(item.link.href));
      }
      if (item.children) {
        flatten(item.children);
      }
      if (item.navChildren) {
        flatten(item.navChildren);
      }
    });
  };

  flatten(navItems);
  return flatArray;
};
